import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { StatusBadge, PriorityBadge, DashboardCaseBadge } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { FolderOpen, AlertTriangle, Inbox, CalendarClock, CheckCircle2, Clock, Headphones, LayoutDashboard, PhoneCall } from "lucide-react";

const KpiCard = ({ label, value, icon: Icon, tone = "blue", sub, onClick }) => {
  const tones = {
    blue: "var(--brand-blue-100)|var(--brand-blue-700)",
    orange: "var(--brand-orange-100)|var(--brand-orange-600)",
    green: "var(--brand-green-100)|var(--brand-green-700)",
    red: "var(--brand-red-100)|var(--brand-red-600)",
  };
  const [bg, fg] = tones[tone].split("|");
  return (
    <Card
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`p-4 ${onClick ? "cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-blue-600)]" : ""}`}
      data-testid="dashboard-kpi-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="font-display text-3xl font-semibold mt-1">{value ?? 0}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: bg }}>
          <Icon className="h-6 w-6" style={{ color: fg }} />
        </div>
      </div>
    </Card>
  );
};

const ChartCard = ({ title, data }) => (
  <Card className="p-4 min-w-0">
    <h3 className="font-semibold mb-3">{title}</h3>
    {data.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">Nessun dato</p> : (
      <>
        <ResponsiveContainer width="100%" height={200} data-testid="dashboard-chart">
          <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="valore" radius={[6, 6, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={["#1976D2", "#F57C00", "#3AAA35", "#0B3A75", "#7A3E00", "#4CAF50"][i % 6]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <table className="w-full text-sm mt-2" data-testid="dashboard-chart-table">
          <caption className="sr-only">{title}</caption>
          <tbody>
            {data.map((d) => (
              <tr key={d.nome} className="border-t"><td className="py-1 text-slate-600">{d.nome}</td><td className="py-1 text-right font-medium">{d.valore}</td></tr>
            ))}
          </tbody>
        </table>
      </>
    )}
  </Card>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <LoadingState />;
  if (!data) return <EmptyState title="Dashboard non disponibile" />;
  const k = data.kpi;

  return (
    <div>
      <PageHeader icon={LayoutDashboard} title={`Benvenuto, ${user?.nome_completo?.split(" ")[0] || ""}`}
        description={`Ruolo: ${user?.ruolo_label || user?.ruolo} · ${fmtDate(new Date().toISOString())}`} />

      {/* Banner Enti associati all'utente (logo + denominazione in colonne) */}
      {Array.isArray(data.miei_enti) && data.miei_enti.length > 0 && (
        <Card className="p-4 mb-4" data-testid="dashboard-enti-banner">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">I tuoi enti</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.miei_enti.map((e) => (
              <div key={e.id} className="flex flex-col items-center text-center gap-2" data-testid={`dashboard-ente-${e.id}`}>
                {e.has_logo ? (
                  <img src={`${API_BASE}/public/entities/${e.id}/logo`} alt={`Logo ${e.denominazione}`} className="h-16 w-16 rounded-lg object-contain border bg-white p-1" />
                ) : (
                  <div className="h-16 w-16 rounded-lg border bg-slate-50 flex items-center justify-center" aria-hidden="true">
                    <span className="font-display text-lg font-semibold text-slate-400">{(e.denominazione || "?").slice(0, 2).toUpperCase()}</span>
                  </div>
                )}
                <span className="text-xs font-medium leading-snug text-slate-700 line-clamp-2">{e.denominazione}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Hero CTA: Modalità Centralino come azione primaria */}
      <Card className="ras-hero-bg border p-5 sm:p-6 mb-4 overflow-hidden" data-testid="dashboard-centralino-hero">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange-100)] px-2.5 py-0.5 text-[11px] font-medium text-[color:var(--brand-orange-600)]">
              <Headphones className="h-3.5 w-3.5" /> Priorità operativa · consigliato durante la chiamata
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-2 text-slate-900">Modalità Centralino</h2>
            <p className="text-sm text-slate-600 mt-1">Registra rapidamente un nuovo contatto e descrivi in dettaglio la situazione in ingresso dell'utenza.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:justify-end shrink-0">
            <Button onClick={() => navigate("/app/centralino")}
              className="h-12 min-h-[48px] px-6 rounded-xl bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white shadow-sm transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
              data-testid="dashboard-centralino-primary-cta">
              <PhoneCall className="h-5 w-5 mr-2" /> Avvia registrazione contatto
            </Button>
            <Button variant="outline" onClick={() => navigate("/app/pratiche")}
              className="h-12 min-h-[48px] px-4 rounded-xl bg-white/70"
              data-testid="dashboard-centralino-secondary-recent">
              Apri pratiche
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Pratiche aperte" value={k.pratiche_aperte} icon={FolderOpen} tone="blue" onClick={() => navigate("/app/pratiche")} />
        <KpiCard label="Pratiche urgenti" value={k.pratiche_urgenti} icon={AlertTriangle} tone="red" onClick={() => navigate("/app/pratiche?priorita=Urgente")} />
        <KpiCard label="Non assegnate" value={k.non_assegnate} icon={Inbox} tone="orange" onClick={() => navigate("/app/pratiche?non_assegnate=true")} />
        <KpiCard label="Nuove (7 giorni)" value={k.nuove_7gg} icon={Clock} tone="green" onClick={() => navigate("/app/pratiche?stato=Nuova")} />
        <KpiCard label="Attività scadute" value={k.attivita_scadute} icon={AlertTriangle} tone="red" onClick={() => navigate("/app/attivita?filtro=scadute")} />
        <KpiCard label="Attività di oggi" value={k.attivita_oggi} icon={CalendarClock} tone="orange" onClick={() => navigate("/app/attivita?filtro=oggi")} />
        <KpiCard label="Appuntamenti oggi" value={k.appuntamenti_oggi} icon={CalendarClock} tone="blue" onClick={() => navigate("/app/agenda?oggi=1")} />
        <KpiCard label="Concluse" value={k.concluse} icon={CheckCircle2} tone="green"
          sub={k.tempo_medio_chiusura_giorni != null ? `Tempo medio: ${k.tempo_medio_chiusura_giorni} gg` : null}
          onClick={() => navigate("/app/pratiche?stato=Conclusa")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-4 min-w-0">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Ultime pratiche</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/pratiche")}>Vedi tutte</Button></div>
          {data.liste.pratiche_recenti.length === 0 ? <EmptyState title="Nessuna pratica" /> : (
            <div className="space-y-2">
              {data.liste.pratiche_recenti.map((c) => (
                <button key={c.id} onClick={() => navigate(`/app/pratiche/${c.id}`)} className="w-full text-left rounded-lg border p-3 hover:bg-muted transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{c.numero}</span>
                    <DashboardCaseBadge stato={c.stato} priorita={c.priorita} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{c.persona_nome} · {c.servizio_nome}</div>
                  {c.gestore_nome && <div className="text-xs mt-0.5 truncate"><span className="text-muted-foreground">Gestore: </span><span className="font-semibold text-[color:var(--brand-green-700)]">{c.gestore_nome}</span></div>}
                </button>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Persone da richiamare</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/attivita")}>Attività</Button></div>
          {data.liste.da_richiamare.length === 0 ? <EmptyState title="Nessun richiamo in agenda" /> : (
            <div className="space-y-2">
              {data.liste.da_richiamare.map((t) => (
                <div key={t.id} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{t.titolo}</span>
                    <PriorityBadge priorita={t.priorita} />
                  </div>
                  <div className="text-xs mt-1 flex flex-wrap items-center gap-x-2">
                    {t.persona_nome && <span className="font-semibold text-[color:var(--brand-blue-700)]">{t.persona_nome}</span>}
                    {t.assegnatario_nome && <span><span className="text-muted-foreground">Gestore: </span><span className="font-semibold text-[color:var(--brand-green-700)]">{t.assegnatario_nome}</span></span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Scadenza: {fmtDateTime(t.data_scadenza)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Statistiche spostate in fondo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <ChartCard title="Pratiche per stato" data={data.grafici.per_stato} />
        <ChartCard title="Pratiche per servizio" data={data.grafici.per_servizio} />
      </div>
    </div>
  );
};

export default Dashboard;
